import {
  AddPhotoAlternate,
  CloudUpload,
  Close,
  ContentCopy,
  DeleteOutline,
  Download,
  Edit,
  PhotoLibrary,
  Print,
  QrCode,
  Security,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

import AppBarSpacer from '../../components/common/AppBarSpacer';
import MiniDrawer from '../../components/MiniDrawer';
import PageHeader from '../../components/common/PageHeader';
import { useTranslation } from '../../i18n';
import {
  deleteParentLookupImage,
  editParentLookupImage,
  getParentLookupImages,
  getParentLookupSetting,
  updateParentLookupImage,
  updateParentLookupSetting,
  uploadParentLookupImage,
  resolveParentLookupImageUrl,
  type ParentLookupSetting as ParentLookupSettingData,
} from '../../utils/parentLookupApi';
import type { ParentLookupImage } from '../../types/ParentLookup.type';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';

const QR_CODE_ID = 'parent-lookup-qr-code';

const formatImageSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ParentLookupSetting = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [setting, setSetting] = useState<ParentLookupSettingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle',
  );
  const [images, setImages] = useState<ParentLookupImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [imageError, setImageError] = useState('');
  const [posterDialogOpen, setPosterDialogOpen] = useState(false);
  const [editingImage, setEditingImage] =
    useState<ParentLookupImage | null>(null);
  const [posterName, setPosterName] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [updatingImageId, setUpdatingImageId] = useState<number | null>(null);
  const [deletingImageId, setDeletingImageId] = useState<number | null>(null);
  const [previewImage, setPreviewImage] =
    useState<ParentLookupImage | null>(null);

  const parentLookupUrl = useMemo(
    () => new URL('/parent-lookup', window.location.origin).toString(),
    [],
  );

  const loadSetting = useCallback(async () => {
    setLoading(true);
    try {
      setSetting(await getParentLookupSetting());
      setError('');
    } catch {
      setError(t('parentLookupSetting.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadSetting();
  }, [loadSetting]);

  const loadImages = useCallback(async () => {
    setImagesLoading(true);
    try {
      setImages(await getParentLookupImages());
      setImageError('');
    } catch {
      setImageError(t('parentLookupSetting.imagesLoadError'));
    } finally {
      setImagesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadImages();
  }, [loadImages]);

  const resetPosterForm = () => {
    setPosterDialogOpen(false);
    setEditingImage(null);
    setPosterName('');
    setSelectedImageFile(null);
    setImageError('');
  };

  const handleToggle = async (captchaRequired: boolean) => {
    if (!setting) return;

    setSaving(true);
    try {
      const updated = await updateParentLookupSetting(captchaRequired);
      setSetting(updated);
      notifyAPIClient(t('parentLookupSetting.saved'));
    } catch {
      notifyFailure();
    } finally {
      setSaving(false);
    }
  };

  const handlePosterSave = async () => {
    const imageBeingEdited = editingImage;
    const trimmedPosterName = posterName.trim();
    if (!trimmedPosterName) {
      setImageError(t('parentLookupSetting.posterNameRequired'));
      return;
    }
    if (!imageBeingEdited && !selectedImageFile) {
      setImageError(t('parentLookupSetting.posterImageRequired'));
      return;
    }

    setUploadingImage(true);
    try {
      const savedImage = imageBeingEdited
        ? await editParentLookupImage(
            imageBeingEdited.id,
            trimmedPosterName,
            selectedImageFile,
          )
        : await uploadParentLookupImage(
            trimmedPosterName,
            selectedImageFile as File,
          );
      setImages((current) =>
        imageBeingEdited
          ? current.map((image) =>
              image.id === savedImage.id ? savedImage : image,
            )
          : [savedImage, ...current],
      );
      resetPosterForm();
      notifyAPIClient(
        t(
          imageBeingEdited
            ? 'parentLookupSetting.imageEdited'
            : 'parentLookupSetting.imageUploaded',
        ),
      );
    } catch {
      setImageError(
        t(
          imageBeingEdited
            ? 'parentLookupSetting.imageEditError'
            : 'parentLookupSetting.imageUploadError',
        ),
      );
      notifyFailure();
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageToggle = async (image: ParentLookupImage) => {
    setUpdatingImageId(image.id);
    try {
      const updatedImage = await updateParentLookupImage(
        image.id,
        !image.isActive,
      );
      setImages((current) =>
        current.map((item) =>
          item.id === updatedImage.id
            ? updatedImage
            : updatedImage.isActive
              ? { ...item, isActive: false }
              : item,
        ),
      );
      setImageError('');
    } catch {
      setImageError(t('parentLookupSetting.imageUpdateError'));
      notifyFailure();
    } finally {
      setUpdatingImageId(null);
    }
  };

  const handleImageDelete = async (image: ParentLookupImage) => {
    if (!window.confirm(t('parentLookupSetting.deletePosterConfirm'))) return;

    setDeletingImageId(image.id);
    try {
      await deleteParentLookupImage(image.id);
      setImages((current) => current.filter((item) => item.id !== image.id));
      if (editingImage?.id === image.id) resetPosterForm();
      notifyAPIClient(t('parentLookupSetting.imageDeleted'));
    } catch {
      setImageError(t('parentLookupSetting.imageDeleteError'));
      notifyFailure();
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(parentLookupUrl);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };

  const handleDownloadQr = () => {
    const canvas = document.getElementById(
      QR_CODE_ID,
    ) as HTMLCanvasElement | null;
    if (!canvas) return;

    const downloadLink = document.createElement('a');
    downloadLink.download = 'aquatics-hub-parent-lookup-qr.png';
    downloadLink.href = canvas.toDataURL('image/png');
    downloadLink.click();
  };

  return (
    <div className="flex h-screen">
      <MiniDrawer open={open} setOpen={setOpen} />
      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title={t('parentLookupSetting.title')}
            subtitle={t('parentLookupSetting.subtitle')}
          />

          <Stack spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Card sx={{ p: { xs: 2, md: 4 } }}>
              <Stack spacing={2.5} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <QrCode color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    {t('parentLookupSetting.qrTitle')}
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'white',
                  }}
                >
                  <QRCodeCanvas
                    id={QR_CODE_ID}
                    value={parentLookupUrl}
                    size={280}
                    marginSize={4}
                    bgColor="#ffffff"
                    fgColor="#102138"
                    level="H"
                  />
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  {t('parentLookupSetting.qrDescription')}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {parentLookupUrl}
                </Typography>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="center"
                >
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={handleCopyLink}
                  >
                    {t('parentLookupSetting.copyLink')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadQr}
                  >
                    {t('parentLookupSetting.downloadQr')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Print />}
                    onClick={() => window.print()}
                  >
                    {t('parentLookupSetting.printQr')}
                  </Button>
                </Stack>

                {copyState === 'copied' && (
                  <Alert severity="success" sx={{ width: '100%' }}>
                    {t('parentLookupSetting.linkCopied')}
                  </Alert>
                )}
                {copyState === 'error' && (
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    {t('parentLookupSetting.copyError')}
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card sx={{ p: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Security color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    {t('parentLookupSetting.securityTitle')}
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  {t('parentLookupSetting.securityDescription')}
                </Typography>

                <Box>
                  <Typography variant="overline" color="text.secondary">
                    PARENT_LOOKUP_ALLOW_MOCK
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={setting?.captchaRequired ?? false}
                        onChange={(event) =>
                          void handleToggle(event.target.checked)
                        }
                        disabled={loading || saving || !setting}
                      />
                    }
                    label={
                      setting?.captchaRequired
                        ? t('parentLookupSetting.cloudflareOn')
                        : t('parentLookupSetting.cloudflareOff')
                    }
                  />
                </Box>

                {loading && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      {t('parentLookupSetting.loading')}
                    </Typography>
                  </Stack>
                )}
                {error && (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => void loadSetting()}
                      >
                        {t('common.retry')}
                      </Button>
                    }
                  >
                    {error}
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card sx={{ p: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhotoLibrary color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    {t('parentLookupSetting.imagesTitle')}
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  {t('parentLookupSetting.imagesDescription')}
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<AddPhotoAlternate />}
                  onClick={() => {
                    resetPosterForm();
                    setPosterDialogOpen(true);
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {t('parentLookupSetting.addPoster')}
                </Button>

                <Typography variant="caption" color="text.secondary">
                  {t('parentLookupSetting.newPosterVisibilityHint')}
                </Typography>

                {imageError && !posterDialogOpen && (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => void loadImages()}
                      >
                        {t('common.retry')}
                      </Button>
                    }
                  >
                    {imageError}
                  </Alert>
                )}

                {imagesLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      {t('parentLookupSetting.imagesLoading')}
                    </Typography>
                  </Stack>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" sx={{ minWidth: 680 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t('parentLookupSetting.posterNameColumn')}
                          </TableCell>
                          <TableCell>
                            {t('parentLookupSetting.previewColumn')}
                          </TableCell>
                          <TableCell>
                            {t('parentLookupSetting.fileColumn')}
                          </TableCell>
                          <TableCell>
                            {t('parentLookupSetting.statusColumn')}
                          </TableCell>
                          <TableCell align="right">
                            {t('parentLookupSetting.actionsColumn')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {images.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                                sx={{ py: 2 }}
                              >
                                {t('parentLookupSetting.noImages')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          images.map((image) => (
                            <TableRow key={image.id} hover>
                              <TableCell>
                                <Typography fontWeight={600}>
                                  {image.posterName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    position: 'relative',
                                    width: 96,
                                    height: 72,
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={resolveParentLookupImageUrl(image.url)}
                                    alt={image.posterName}
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      display: 'block',
                                      borderRadius: 1,
                                      bgcolor: 'action.hover',
                                    }}
                                  />
                                  <Tooltip
                                    title={t(
                                      'parentLookupSetting.previewImage',
                                    )}
                                  >
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      aria-label={t(
                                        'parentLookupSetting.previewImage',
                                      )}
                                      onClick={() => setPreviewImage(image)}
                                      sx={{
                                        position: 'absolute',
                                        right: 4,
                                        bottom: 4,
                                        bgcolor: 'background.paper',
                                        '&:hover': {
                                          bgcolor: 'background.paper',
                                        },
                                      }}
                                    >
                                      <Visibility fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap>
                                  {image.originalName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatImageSize(image.sizeBytes)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={image.isActive ? 'success' : 'default'}
                                  label={
                                    image.isActive
                                      ? t('parentLookupSetting.imageActive')
                                      : t('parentLookupSetting.imageInactive')
                                  }
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <FormControlLabel
                                    sx={{ m: 0 }}
                                    control={
                                      <Switch
                                        size="small"
                                        checked={image.isActive}
                                        onChange={() =>
                                          void handleImageToggle(image)
                                        }
                                        disabled={
                                          updatingImageId === image.id ||
                                          deletingImageId === image.id
                                        }
                                      />
                                    }
                                    label=""
                                  />
                                  <Tooltip
                                    title={t('parentLookupSetting.editPoster')}
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label={t(
                                          'parentLookupSetting.editPoster',
                                        )}
                                        onClick={() => {
                                          setEditingImage(image);
                                          setPosterName(image.posterName);
                                          setSelectedImageFile(null);
                                          setImageError('');
                                          setPosterDialogOpen(true);
                                        }}
                                        disabled={
                                          uploadingImage ||
                                          deletingImageId === image.id
                                        }
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip
                                    title={t('parentLookupSetting.deletePoster')}
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        aria-label={t(
                                          'parentLookupSetting.deletePoster',
                                        )}
                                        onClick={() =>
                                          void handleImageDelete(image)
                                        }
                                        disabled={
                                          deletingImageId === image.id ||
                                          uploadingImage
                                        }
                                      >
                                        <DeleteOutline fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </Card>
          </Stack>
        </div>

        <Dialog
          open={posterDialogOpen}
          onClose={() => {
            if (!uploadingImage) resetPosterForm();
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {t(
              editingImage
                ? 'parentLookupSetting.editPosterTitle'
                : 'parentLookupSetting.addPosterTitle',
            )}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                autoFocus
                fullWidth
                required
                label={t('parentLookupSetting.posterNameLabel')}
                placeholder={t('parentLookupSetting.posterNamePlaceholder')}
                value={posterName}
                onChange={(event) => {
                  setPosterName(event.target.value);
                  setImageError('');
                }}
                inputProps={{ maxLength: 160 }}
              />
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                disabled={uploadingImage}
              >
                {t(
                  editingImage
                    ? 'parentLookupSetting.chooseReplacementImage'
                    : 'parentLookupSetting.chooseImage',
                )}
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp,image/bmp,image/avif"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setSelectedImageFile(file);
                    setImageError('');
                    event.target.value = '';
                  }}
                />
              </Button>
              <Typography
                variant="body2"
                color={selectedImageFile ? 'text.primary' : 'text.secondary'}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {selectedImageFile
                  ? `${selectedImageFile.name} (${formatImageSize(selectedImageFile.size)})`
                  : t('parentLookupSetting.noImageSelected')}
              </Typography>
              {imageError && <Alert severity="error">{imageError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetPosterForm} disabled={uploadingImage}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void handlePosterSave()}
              disabled={uploadingImage}
              startIcon={
                uploadingImage ? (
                  <CircularProgress size={18} />
                ) : (
                  <CloudUpload />
                )
              }
            >
              {uploadingImage
                ? t('parentLookupSetting.uploadingImage')
                : t(
                    editingImage
                      ? 'parentLookupSetting.savePoster'
                      : 'parentLookupSetting.addPoster',
                  )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(previewImage)}
          onClose={() => setPreviewImage(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {previewImage?.posterName}
              </Typography>
              {previewImage && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {previewImage.originalName}
                </Typography>
              )}
            </Box>
            <IconButton
              aria-label={t('common.close')}
              onClick={() => setPreviewImage(null)}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ textAlign: 'center' }}>
            {previewImage && (
              <Box
                component="img"
                src={resolveParentLookupImageUrl(previewImage.url)}
                alt={previewImage.posterName}
                sx={{
                  display: 'block',
                  width: '100%',
                  maxHeight: '75vh',
                  objectFit: 'contain',
                  mx: 'auto',
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ParentLookupSetting;
